"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const faBan_1 = require("@fortawesome/free-solid-svg-icons/faBan");
const faCheck_1 = require("@fortawesome/free-solid-svg-icons/faCheck");
const faCheckDouble_1 = require("@fortawesome/free-solid-svg-icons/faCheckDouble");
const faTriangleExclamation_1 = require("@fortawesome/free-solid-svg-icons/faTriangleExclamation");
const faRotate_1 = require("@fortawesome/free-solid-svg-icons/faRotate");
const useObjectReview_1 = require("../../../services/admin/useObjectReview");
const reviewDecisionButtonsState_1 = require("../../../services/admin/reviewDecisionButtonsState");
/**
 * Approve/reject links for the interview itself, in the style of the other links of the
 * interview top menu. The interview is the parent of every other survey object, so its
 * decisions belong next to the completion links rather than in a survey object box.
 * @param props.interviewUuid - Uuid of the reviewed interview
 * @returns Review links, or null when interviews are not reviewable in this survey
 */
const InterviewReviewLinks = ({ interviewUuid }) => {
    const { t } = (0, react_i18next_1.useTranslation)('admin');
    const review = (0, useObjectReview_1.useObjectReview)('interview', interviewUuid);
    const state = (0, reviewDecisionButtonsState_1.getReviewDecisionButtonsState)(review.status);
    // The interview has no parent. A rejected object below does not hide this approve link.
    if (!review.hasReviewControls) {
        return null;
    }
    const renderToggleLink = ({ labelKey, icon, colorClass, isPressed, onActivate, onClear }) => (react_1.default.createElement("a", { href: "#", className: `${colorClass}${isPressed ? ' _active-background' : ''}`, title: t(labelKey), "aria-label": t(labelKey), "aria-pressed": isPressed, onClick: (e) => {
            e.preventDefault();
            // Clicking the pressed decision again clears it, unless it was force-approved.
            if (isPressed && onClear) {
                onClear();
                return;
            }
            onActivate();
        } },
        react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: icon })));
    return (react_1.default.createElement(react_1.default.Fragment, null,
        state.askedToReReview && (react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faRotate_1.faRotate, className: "_yellow", title: t('interviewMember.reReviewRequested'), "aria-label": t('interviewMember.reReviewRequested'), role: "img" })),
        state.showConflictWarning && (react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faTriangleExclamation_1.faTriangleExclamation, className: "_yellow", title: t('interviewMember.reviewConflict'), "aria-label": t('interviewMember.reviewConflict'), role: "img" })),
        renderToggleLink({
            labelKey: 'interviewMember.rejectObject',
            icon: faBan_1.faBan,
            colorClass: '_red',
            isPressed: state.rejectPressed,
            onActivate: review.reject,
            onClear: state.canClearDecision ? review.clearReview : undefined
        }),
        react_1.default.createElement(react_1.default.Fragment, null,
            ' ',
            renderToggleLink({
                labelKey: 'interviewMember.approveObject',
                icon: faCheck_1.faCheck,
                colorClass: '_green',
                isPressed: state.approvePressed,
                onActivate: review.approve,
                onClear: state.canClearDecision ? review.clearReview : undefined
            })),
        review.canForceApprove && state.showForceApprove && (react_1.default.createElement(react_1.default.Fragment, null,
            ' ',
            renderToggleLink({
                labelKey: 'interviewMember.forceApproveObject',
                icon: faCheckDouble_1.faCheckDouble,
                colorClass: '_green',
                isPressed: state.forceApprovePressed,
                onActivate: review.forceApprove,
                onClear: review.clearForceApprove
            }))),
        state.showRequestReReview && (react_1.default.createElement(react_1.default.Fragment, null,
            ' ',
            renderToggleLink({
                labelKey: 'interviewMember.requestReReview',
                icon: faRotate_1.faRotate,
                colorClass: '',
                isPressed: state.reReviewPressed,
                onActivate: review.requestReReview
            })))));
};
exports.default = InterviewReviewLinks;
//# sourceMappingURL=InterviewReviewLinks.js.map